// src/ContactPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';

function ContactPage() {
  const { theme } = useTheme();

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.primary.bg} ${theme.primary.text}`}>
      {/* Header */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="p-6 md:p-12 mb-8 text-center"
      >
        <h1 className={`text-5xl font-extrabold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent drop-shadow-2xl mb-4`}>
          Contact Us
        </h1>
        <p className={`text-xl ${theme.primary.textSecondary} ${theme.primary.card} p-3 rounded-xl border inline-block`}>
          Have questions or need support? We're here to help!
        </p>
      </motion.header>

      {/* Contact Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className={`p-6 md:p-12 max-w-4xl mx-auto ${theme.primary.textSecondary}`}
      >
        {/* Section 1: Get in Touch */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            1. Get in Touch
          </h2>
          <p className="leading-relaxed mb-6">
            Reach out to us via the methods below{SITE_CONFIG.display.navbar.discordButton ? ' or use the form to send us a message directly' : ''}. We'll get back to you as soon as possible.
          </p>
          <ul className="list-none space-y-4">
            <li className="flex items-center space-x-3">
              <div className={`w-8 h-8 bg-gradient-to-r ${theme.primary.button} rounded-full flex items-center justify-center`}>
                <span className="text-white text-sm">📧</span>
              </div>
              <div>
                <strong>Email:</strong>{' '}
                <a href={`mailto:${SITE_CONFIG.contact.email}`} className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent hover:underline`}>
                  {SITE_CONFIG.contact.email}
                </a>
              </div>
            </li>
            {SITE_CONFIG.display.navbar.discordButton && (
              <li className="flex items-center space-x-3">
                <div className={`w-8 h-8 bg-gradient-to-r ${theme.primary.button} rounded-full flex items-center justify-center`}>
                  <span className="text-white text-sm">💬</span>
                </div>
                <div>
                  <strong>Discord:</strong> Join our{' '}
                  <a href={SITE_CONFIG.social.discord} className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent hover:underline`}>
                    Discord server
                  </a>{' '}
                  and message an admin
                </div>
              </li>
            )}
          </ul>
        </motion.section>

        {/* Section 3: Response Time */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            3. Response Time
          </h2>
          <p className="leading-relaxed">
            We aim to respond to all inquiries within 48 hours. For urgent issues, please mention "URGENT" in your message subject{SITE_CONFIG.display.navbar.discordButton ? ' or tag an admin on Discord' : ''}.
          </p>
        </motion.section>

        {/* Section 4: Additional Help */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            4. Additional Help
          </h2>
          <p className="leading-relaxed">
            Check out our{' '}
            <Link to="/faq" className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent hover:underline`}>
              FAQ page
            </Link>{' '}
            for quick answers to common questions, or review our{' '}
            <Link to="/privacy" className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent hover:underline`}>
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link to="/terms" className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent hover:underline`}>
              Terms and Conditions
            </Link>{' '}
            for more details about using {SITE_CONFIG.brandName}.
          </p>
        </motion.section>
      </motion.div>

      {/* Back to Home Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="p-6 md:p-12 mt-8 text-center"
      >
        <Link
          to="/"
          className={`bg-gradient-to-r ${theme.primary.button} text-white px-8 py-4 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 shadow-2xl font-semibold text-lg inline-block`}
        >
          Back to Home
        </Link>
      </motion.div>
    </div>
  );
}

export default ContactPage;
